import React, { useEffect, useRef, useState, ChangeEvent } from 'react';
import axios from 'ps-components/lib/apiClient';
import fileDownload from 'js-file-download';
//Components
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import { Button, Select } from '../../../../../../ps-aspect-design';
import { useToast, ToastProps } from 'ps-shared-ui/v4/Toast';
import Search from 'ps-components/Search/Search';
import { getGenericRow } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import SuperAdminListActionCol from './SuperAdminListActionCol';
import { ISuperAdmin, ISuperAdminListState, SuperAdminStatus } from '../../../../../../lib/types'

const toastConfig: ToastProps = {
  duration: 10000,
  isCloseable: true,
  type: 'error'
};

function SuperAdminList() {
  const [state, setStateNative] = useState<ISuperAdminListState>({ superAdminList: [], filteredList: [], search: '', status: 'all', loading: false });
  const setState = (obj: Partial<ISuperAdminListState>) => setStateNative(s => ({ ...s, ...obj }));
  const { superAdminList, filteredList, search, status, loading } = state;
  const ref = useRef()
  const searchRef = useRef()
  const toast = useToast();

  useEffect(() => {
    getSuperAdmins();
  }, []);

  const getSuperAdmins = async () => {
    setState({ loading: true });
    try {
      const res = await axios.apiGet('/api/generic-user/superadmin/list');

      if (res.data.success) setState({ superAdminList: res.data.superAdmins, filteredList: applyFilters(res.data.superAdmins, search, status) });
      else toast(res.data.message || 'There was a problem fetching superadmin users', toastConfig);
    } catch (err) {
      toast('Could not get super admin list ' + err, toastConfig);
    }
    setState({ loading: false });
  };

  const downloadToExcel = async () => {
    try {
      const response = await axios.apiPost('/api/util/report-print/superAdminToExcel', { superAdmins: filteredList }, { responseType: 'arraybuffer' })
      fileDownload(response.data, 'superadmins.xlsx');
    } catch (err) {
      console.error(err)
      toast('Error Exporting Super Admin List to Excel', toastConfig)
    }
  }

  const applyFilters = (superAdminList: ISuperAdmin[], search: string, status: SuperAdminStatus) => {
    if (status !== 'all') return superAdminList.filter(user => !!user.email && user.member_state === status && user.email.toLowerCase().includes(search.toLowerCase()))
    return superAdminList.filter(user => !!user.email && user.email.toLowerCase().includes(search.toLowerCase()))
  }

  const handleChange = (searchValue: string) => {
    setState({ search: searchValue, filteredList: applyFilters(superAdminList, searchValue, status) })
  }

  return (
    <div className="superadminlist">
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex' }}>
          <div>
            <Search
              style={{ width: 400 }}
              value={search}
              onClick={() => handleChange('')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
              placeholder="Search"
            />
          </div>
          <div style={{ width: 150 }}>
            <Select
              options={[
                { text: 'all', key: 'all' },
                { text: 'approved', key: 'approved' },
                { text: 'banned', key: 'banned' }
              ]}
              componentRef={ref as any}
              value={status}
              handleChange={(newStatus: SuperAdminStatus) => setState({ status: newStatus, filteredList: applyFilters(superAdminList, search, newStatus) })}
            />
          </div>
        </div>
        <Button onClick={downloadToExcel}>Export to Excel</Button>
      </div>

      <div style={{ height: '80vh', overflow: 'scroll' }}>
        <AdvancedTable
          stickyHeader
          columnHeaders={[
            { Header: 'Email', width: 275, accessor: 'email' },
            { Header: 'User Id', width: 100, accessor: 'user_id' },
            { Header: 'Last Visit', width: 220, accessor: 'last_visit' },
            { Header: 'Status', accessor: 'member_state' },
            {
              Header: 'Manage Super Admins',
              accessor: 'superAdminWhiteList',
              customRender: (row: ISuperAdmin) => <span>{row.superAdminWhiteList && 'Yes'}</span>
            },
            {
              Header: 'Site Creation',
              accessor: 'siteCreationWhiteList',
              customRender: (row: ISuperAdmin) => <span>{row.siteCreationWhiteList && 'Yes'}</span>
            },
            {
              Header: 'Unique Ranges',
              accessor: 'uniqueRangesWhiteList',
              customRender: (row: ISuperAdmin) => <span>{row.uniqueRangesWhiteList && 'Yes'}</span>
            },
            { Header: 'Action', width: 340, customRender: (row: ISuperAdmin) => <SuperAdminListActionCol row={row} getSuperAdmins={getSuperAdmins} setState={setState} /> }
          ]}
          rowData={filteredList}
          getDataRow={getGenericRow}
          dataLoading={loading}
        />
      </div>
    </div>
  );
}

export default SuperAdminList;
